import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./Template.css";
import { publicApiClient } from "../../../../api/apiClient";

const COUNCIL_INTRO_API = "/api/council/intro";

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10).replaceAll("-", ".");
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}.${mm}.${dd}`;
};

const getListFromResponse = (result) => {
  const data = result?.data || result;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.content)) return data.data.content;

  return [];
};

const getFileId = (file) => {
  if (!file) return null;

  return (
    file.noticeFileId ||
    file.activityNoticeFileId ||
    file.activityFileId ||
    file.fileId ||
    file.attachmentId ||
    file.id ||
    null
  );
};

const getFileName = (file) => {
  if (!file) return "";

  return (
    file.fileName ||
    file.originalFileName ||
    file.name ||
    file.originalName ||
    file.uploadFileName ||
    "첨부파일"
  );
};

const getFileUrl = (file) => {
  if (!file) return "";

  return file.fileUrl || file.downloadUrl || file.url || "";
};

const getFileSize = (file) => {
  if (!file) return "";

  return file.fileSize || file.originalFileSize || file.size || "";
};

const normalizeFiles = (item = {}) => {
  const fileList = [];

  const possibleArrays = [
    item.files,
    item.fileList,
    item.noticeFiles,
    item.activityNoticeFiles,
    item.attachments,
    item.attachmentList,
  ];

  possibleArrays.forEach((array) => {
    if (Array.isArray(array)) {
      fileList.push(...array);
    }
  });

  const possibleSingleFiles = [
    item.file,
    item.noticeFile,
    item.activityNoticeFile,
    item.attachment,
  ];

  possibleSingleFiles.forEach((file) => {
    if (file && typeof file === "object") {
      fileList.push(file);
    }
  });

  if (fileList.length === 0 && (item.fileName || item.fileUrl)) {
    fileList.push({
      fileId:
        item.noticeFileId ||
        item.activityNoticeFileId ||
        item.activityFileId ||
        item.fileId ||
        item.attachmentId ||
        null,
      fileName: item.fileName || item.originalFileName || "첨부파일",
      fileUrl: item.fileUrl || item.downloadUrl || item.url || "",
      fileSize: item.fileSize || item.originalFileSize || "",
    });
  }

  const uniqueMap = new Map();

  fileList.forEach((file, index) => {
    const fileId = getFileId(file);
    const fileName = getFileName(file);
    const fileUrl = getFileUrl(file);
    const fileSize = getFileSize(file);

    const key = fileId || `${fileName}-${index}`;

    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, {
        fileId,
        fileName,
        fileUrl,
        fileSize,
      });
    }
  });

  return Array.from(uniqueMap.values()).filter((file) => file.fileName);
};

const getImageId = (image) => {
  if (!image) return null;

  return (
    image.noticeImageId ||
    image.activityNoticeImageId ||
    image.activityImageId ||
    image.imageId ||
    image.fileId ||
    image.id ||
    null
  );
};

const getImageName = (image) => {
  if (!image) return "";

  return (
    image.fileName ||
    image.originalFileName ||
    image.name ||
    image.originalName ||
    image.uploadFileName ||
    "공지 이미지"
  );
};

const getImageUrl = (image) => {
  if (!image) return "";

  return (
    image.imageUrl ||
    image.fileUrl ||
    image.url ||
    image.previewUrl ||
    image.thumbnailUrl ||
    image.mainImageUrl ||
    ""
  );
};

const normalizeImages = (item = {}) => {
  const imageList = [];

  const possibleArrays = [
    item.images,
    item.imageList,
    item.noticeImages,
    item.activityNoticeImages,
    item.activityImages,
    item.imageFiles,
    item.imageFileList,
  ];

  possibleArrays.forEach((array) => {
    if (Array.isArray(array)) {
      imageList.push(...array);
    }
  });

  const possibleSingleImages = [
    item.image,
    item.noticeImage,
    item.activityNoticeImage,
    item.activityImage,
    item.mainImage,
    item.thumbnail,
  ];

  possibleSingleImages.forEach((image) => {
    if (image && typeof image === "object") {
      imageList.push(image);
    }
  });

  if (
    imageList.length === 0 &&
    (item.imageUrl ||
      item.fileUrl ||
      item.thumbnailUrl ||
      item.mainImageUrl)
  ) {
    imageList.push({
      imageId:
        item.noticeImageId ||
        item.activityNoticeImageId ||
        item.activityImageId ||
        item.imageId ||
        null,
      imageUrl:
        item.imageUrl ||
        item.fileUrl ||
        item.thumbnailUrl ||
        item.mainImageUrl ||
        "",
      fileName:
        item.fileName ||
        item.originalFileName ||
        item.imageName ||
        "공지 이미지",
    });
  }

  const uniqueMap = new Map();

  imageList.forEach((image, index) => {
    const imageId = getImageId(image);
    const imageName = getImageName(image);
    const imageUrl = getImageUrl(image);

    if (!imageUrl) return;

    const key = imageId || `${imageUrl}-${index}`;

    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, {
        imageId,
        imageName,
        imageUrl,
      });
    }
  });

  return Array.from(uniqueMap.values());
};

const getCouncilProfileImageUrl = (data = {}) => {
  return (
    data.profileImg ||
    data.profileImage ||
    data.profileImageUrl ||
    data.profileUrl ||
    data.logoUrl ||
    data.imageUrl ||
    data.mainImgUrl ||
    data.mainImg ||
    data.mainImageUrl ||
    data.introImg ||
    data.introImage ||
    ""
  );
};

const normalizeNotice = (item, fallbackId) => ({
  id:
    item.id ||
    item.noticeId ||
    item.activityNoticeId ||
    item.activityId ||
    fallbackId,
  title: item.title || "",
  writer:
    item.writer ||
    item.userName ||
    item.createdBy ||
    item.author ||
    "총학생회",
  date: formatDate(
    item.date || item.createdAt || item.createdDate || item.updatedAt
  ),
  rawDate: item.date || item.createdAt || item.createdDate || item.updatedAt || "",
  view: item.viewer ?? item.view ?? item.views ?? item.viewCount ?? 0,
  content: item.content || "",
  files: normalizeFiles(item),
  images: normalizeImages(item),
});

const normalizeListItem = (item) => ({
  id: item.id || item.noticeId || item.activityNoticeId || item.activityId,
  title: item.title || "",
  rawDate: item.date || item.createdAt || item.createdDate || item.updatedAt || "",
});

const sortOldestFirst = (a, b) => {
  const dateA = new Date(a.rawDate).getTime();
  const dateB = new Date(b.rawDate).getTime();

  if (!Number.isNaN(dateA) && !Number.isNaN(dateB)) {
    return dateA - dateB;
  }

  return Number(a.id || 0) - Number(b.id || 0);
};

export default function TemplateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [item, setItem] = useState(null);
  const [prevItem, setPrevItem] = useState(null);
  const [nextItem, setNextItem] = useState(null);
  const [councilProfileImg, setCouncilProfileImg] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCouncilIntro = async () => {
      try {
        const result = await publicApiClient(COUNCIL_INTRO_API, {
          method: "GET",
        });

        const data = result?.data || result;

        setCouncilProfileImg(getCouncilProfileImageUrl(data));
      } catch (error) {
        console.error("총학생회 소개 이미지 조회 실패:", error);
        setCouncilProfileImg("");
      }
    };

    fetchCouncilIntro();
  }, []);

  useEffect(() => {
    const fetchNoticeDetail = async () => {
      if (!id) return;

      try {
        setLoading(true);

        const detailResult = await publicApiClient(`/api/activity/notice/${id}`, {
          method: "GET",
        });

        const detailData = detailResult?.data || detailResult;

        if (!detailData) {
          setItem(null);
          return;
        }

        setItem(normalizeNotice(detailData, id));

        const listResult = await publicApiClient(
          "/api/activity/notice?searchCategory=TITLE&page=0&size=100",
          {
            method: "GET",
          }
        );

        const list = getListFromResponse(listResult)
          .map(normalizeListItem)
          .filter((notice) => notice.id)
          .sort(sortOldestFirst);

        const currentIndex = list.findIndex(
          (notice) => String(notice.id) === String(id)
        );

        setPrevItem(currentIndex > 0 ? list[currentIndex - 1] : null);

        setNextItem(
          currentIndex >= 0 && currentIndex < list.length - 1
            ? list[currentIndex + 1]
            : null
        );
      } catch (error) {
        console.error("공지사항 상세 조회 실패:", error);
        setItem(null);
        setPrevItem(null);
        setNextItem(null);
      } finally {
        setLoading(false);
      }
    };

    fetchNoticeDetail();
  }, [id]);

  if (loading) {
    return <div className="notice-detail-not-found">불러오는 중입니다.</div>;
  }

  if (!item) {
    return (
      <div className="notice-detail-not-found">
        게시글을 찾을 수 없습니다.
      </div>
    );
  }

  return (
    <div className="notice-detail-wrap">
      <div className="notice-detail-inner">
        <div className="notice-detail-title">{item.title}</div>

        <div className="notice-detail-meta-author">
          {councilProfileImg && (
            <img
              src={councilProfileImg}
              alt="author"
              className="notice-detail-author-avatar"
            />
          )}

          <span className="notice-detail-author-name">{item.writer}</span>
        </div>

        <div className="notice-detail-meta-row">
          <span>
            <span className="notice-detail-label">작성일</span> {item.date}
          </span>

          <span>
            <span className="notice-detail-label">조회수</span> {item.view}
          </span>
        </div>

        <div className="notice-detail-divider" />

        <div className="notice-detail-body">
          {(item.content || "").split("\n").map((line, index) => (
            <p key={index}>{line || "\u00A0"}</p>
          ))}
        </div>

        {item.images?.length > 0 && (
          <div className="notice-detail-images">
            {item.images.map((image, index) => (
              <img
                key={image.imageId || `${image.imageUrl}-${index}`}
                className="notice-detail-image"
                src={image.imageUrl}
                alt={image.imageName || "공지 이미지"}
              />
            ))}
          </div>
        )}

        {item.files?.length > 0 && (
          <div className="notice-detail-attachments">
            {item.files.map((file, index) => (
              <div
                className="notice-detail-attachment"
                key={file.fileId || `${file.fileName}-${index}`}
              >
                <span className="notice-detail-attach-name">
                  {file.fileName}
                  {file.fileSize ? ` (${file.fileSize})` : ""}
                </span>

                {file.fileUrl ? (
                  <a
                    className="notice-detail-attach-btn"
                    href={file.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={file.fileName || "첨부파일"}
                  >
                    ↓
                  </a>
                ) : (
                  <button className="notice-detail-attach-btn" type="button">
                    ↓
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="notice-detail-nav">
          <div
            className={`notice-detail-nav-item ${!nextItem ? "disabled" : ""}`}
            onClick={() =>
              nextItem && navigate(`/stdcouncil-log/Notice/${nextItem.id}`)
            }
          >
            <span className="notice-detail-nav-arrow">∧</span>

            <span className="notice-detail-nav-text">
              {nextItem ? nextItem.title : "다음 글이 없습니다"}
            </span>
          </div>

          <div
            className={`notice-detail-nav-item ${!prevItem ? "disabled" : ""}`}
            onClick={() =>
              prevItem && navigate(`/stdcouncil-log/Notice/${prevItem.id}`)
            }
          >
            <span className="notice-detail-nav-arrow">∨</span>

            <span className="notice-detail-nav-text">
              {prevItem ? prevItem.title : "이전 글이 없습니다"}
            </span>
          </div>
        </div>

        <div className="notice-detail-back-row">
          <button
            className="notice-detail-back-btn"
            onClick={() => navigate("/stdcouncil-log/Notice")}
            type="button"
          >
            목록
          </button>
        </div>
      </div>
    </div>
  );
}